import type { DefaultOptions, QueryKey, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { logError } from '@zg-rentals/log-error';

export type QueryMeta = {
  context?: Record<string, unknown>;
  tags?: Array<string>;
};

declare const invalidatesFnBrand: unique symbol;

/**
 * `meta` is typed globally via `Register.mutationMeta` (see below), so a raw
 * `MutationMeta.invalidates` function can't be parameterized per-mutation with that
 * mutation's `TVariables`/`TData` — hence `unknown` here. The brand makes this type
 * unreachable except through `invalidatesFor`'s return value, so a hand-written function
 * (which would see `variables`/`data` as `unknown`) is rejected at the `meta.invalidates`
 * call site instead of silently type-checking.
 */
export type InvalidatesFn = ((variables: unknown, data: unknown) => Array<string>) & {
  readonly [invalidatesFnBrand]: true;
};

export type MutationMeta = {
  context?: Record<string, unknown>;
  invalidates?: Array<string | InvalidatesFn>;
};

/**
 * Builds a type-safe `meta.invalidates` value for a specific mutation hook. `TVariables` and
 * `TData` are inferred from `hook` — pass the generated ts-client hook itself (never called, only
 * referenced) so `fn`'s parameters get that hook's real, concrete types instead of `unknown`.
 *
 * Returns a single-element array so it can be used directly as `invalidates`, or spread alongside
 * static tag strings (or another `invalidatesFor` call) when merging a default list with a
 * caller override — no separate combining step needed.
 *
 * @example
 * ```ts
 * useUpdateSupplement({
 *   meta: {
 *     invalidates: invalidatesFor(useUpdateSupplement, (variables, data) => [
 *       'supplementById',
 *       `supplement-${data.id}`,
 *     ]),
 *   },
 * });
 * ```
 */
export function invalidatesFor<TData, TError, TVariables, TContext = unknown>(
  _hook: (
    options?: UseMutationOptions<TData, TError, TVariables, TContext>,
  ) => UseMutationResult<TData, TError, TVariables, TContext>,
  fn: (variables: TVariables, data: TData) => Array<string>,
): Array<InvalidatesFn> {
  return [fn as InvalidatesFn];
}

/**
 * Resolves a `meta.invalidates` value to a concrete tag array, calling any `invalidatesFor`
 * elements with `variables`/`data` and passing static tag strings through as-is. A throwing
 * element is reported via `onResolveError` and contributes no tags, but doesn't drop the tags
 * from the other (static or successfully-resolved) elements in the same array.
 */
function resolveInvalidates(
  invalidates: Array<string | InvalidatesFn> | undefined,
  variables: unknown,
  data: unknown,
  onResolveError: (error: Error) => void,
): Array<string> {
  return (invalidates ?? []).flatMap((item) => {
    if (typeof item !== 'function') return [item];
    try {
      return item(variables, data);
    } catch (resolveError) {
      onResolveError(resolveError instanceof Error ? resolveError : new Error(String(resolveError)));
      return [];
    }
  });
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryMeta;
    mutationMeta: MutationMeta;
  }
}

export type QueryErrorContext = (
  error: Error,
  queryKey: QueryKey,
  queryMeta?: QueryMeta,
) => Parameters<typeof logError>[0]['context'];

export type ShouldLogQueryError = (error: Error, queryKey: QueryKey, queryMeta?: QueryMeta) => boolean;

export type MutationErrorContext = (
  error: Error,
  mutationVariables: unknown,
  mutationMeta?: MutationMeta,
) => Parameters<typeof logError>[0]['context'];

export type CreateQueryClientOptions = {
  defaultOptions?: DefaultOptions;
  queryErrorContext?: QueryErrorContext;
  shouldLogQueryError?: ShouldLogQueryError;
  mutationErrorContext?: MutationErrorContext;
};

export const defaultQueryClientOptions = {
  queries: {
    refetchOnWindowFocus: false,
  },
} satisfies DefaultOptions;

const defaultQueryErrorContext: QueryErrorContext = (_error, queryKey, { context, tags } = {}) => ({
  queryKey,
  tags,
  ...context,
});

const defaultShouldLogQueryError: ShouldLogQueryError = () => true;

/**
 * A mutation that fails never reaches `onSuccess`, so there's no real `data`/`variables` context
 * to resolve a dynamic `invalidatesFor` element with — calling it anyway risks logging a
 * fabricated tag (e.g. a defensively-written `` `v:${data?.version}` `` returning `'v:undefined'`)
 * that reads as a real invalidation target. So each `invalidatesFor` element logs as the literal
 * `'<dynamic>'` in place of calling it; static tag strings in the same array are kept as-is.
 */
export function resolveInvalidatesForLogging(invalidates: MutationMeta['invalidates']): Array<string> | undefined {
  return invalidates?.map((item) => (typeof item === 'function' ? '<dynamic>' : item));
}

const defaultMutationErrorContext: MutationErrorContext = (_error, variables, { context, invalidates } = {}) => ({
  variables,
  invalidates: resolveInvalidatesForLogging(invalidates),
  ...context,
});

export function createQueryClient({
  defaultOptions = defaultQueryClientOptions,
  queryErrorContext = defaultQueryErrorContext,
  shouldLogQueryError = defaultShouldLogQueryError,
  mutationErrorContext = defaultMutationErrorContext,
}: CreateQueryClientOptions = {}) {
  const queryClient = new QueryClient({
    defaultOptions,

    queryCache: new QueryCache({
      onError: (error, { queryKey, meta: queryMeta }) => {
        if (!shouldLogQueryError(error, queryKey, queryMeta)) return;

        logError({ error, context: queryErrorContext(error, queryKey, queryMeta) });
      },
    }),

    mutationCache: new MutationCache({
      onError: (error, variables, _context, { meta: mutationMeta }) => {
        logError({ error, context: mutationErrorContext(error, variables, mutationMeta) });
      },

      onSuccess: (data, variables, _context, { meta: mutationMeta }) => {
        const invalidates = resolveInvalidates(mutationMeta?.invalidates, variables, data, (error) => {
          // Never route through the app's `mutationErrorContext` here: that builder may log raw
          // `variables` (the default one does), and this mutation already succeeded — the bug is
          // in an unrelated `invalidatesFor`, not in the mutation itself, so it must not be able to
          // leak credential-bearing variables that were never at risk before dynamic invalidates
          // existed. `meta.context` is the sanctioned, hand-authored observability channel instead.
          logError({
            error,
            context: { invalidates: resolveInvalidatesForLogging(mutationMeta?.invalidates), ...mutationMeta?.context },
          });
        });

        queryClient.invalidateQueries({
          predicate: ({ meta: queryMeta }) => invalidates.some((tag) => queryMeta?.tags?.includes(tag)),
        });
      },
    }),
  });

  return queryClient;
}
